import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import PageHeader from '../../components/PageHeader'
import Table from '../../components/Table'
import AssetWithdrawalModal, { WithdrawableAsset } from '../../components/AssetWithdrawalModal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import ExportBalanceForm from '../../components/ExportBalanceForm'
import LoadingBar from '../../components/LoadingBar'
import Modal from '../../components/Modal'
import Dots from '../../icons/Dots'
import Treasury from '../../state/Treasury'
import { RADOM_COLORS } from '../../util/Constants'
import { getMethod, getPaymentMethods, ManagedPaymentMethod } from '../../util/Managed'
import { useTestMode, formatCurrency, errorToast, successToast } from '../../util/Util'
import { TokenTitle } from '../Funds'
import User from '../../state/User'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import AssetConversionRFQModal from '../../components/AssetConversionRFQModal'
import Radom, { ConversionPair, IPaymentMethod } from '../../state/Radom'
import ConvertIcon from '../../icons/ConvertIcon'
import Tooltip from '../../components/Tooltip'

const isSamePaymentMethod = (a: IPaymentMethod, b: IPaymentMethod): boolean => {
  return a.network === b?.network && a.token === b?.token
}

interface SwapParams {
  fromNetwork: string,
  fromToken?: string,
  toNetwork?: string,
  toToken?: string,
  amount: number
};

const performSwapEstimate = async ({
  fromNetwork,
  fromToken,
  toNetwork,
  toToken,
  amount
}: SwapParams): Promise<{ estimatedAmount: number }> => {
  let rates = await Radom.getSwapRates({
    from: {
      network: fromNetwork,
      token: fromToken,
    },
    into: {
      network: toNetwork ?? "",
      token: toToken
    },
    amount: amount
  });

  return {
    estimatedAmount: rates.amount
  }
}

const performSwap = async ({
  fromNetwork,
  fromToken,
  toNetwork,
  toToken,
  amount
}: SwapParams): Promise<void> => {
  await Radom.initiateSwap({
    from: {
      network: fromNetwork,
      token: fromToken,
    },
    into: {
      network: toNetwork ?? "",
      token: toToken
    },
    amount: amount
  });
}

const Overview = observer((): ReactElement => {
  const [testMode] = useTestMode()

  const [modalOpen, setModalOpen] = useState(false)
  const [exportBalanceModalOpen, setExportBalanceModalOpen] = useState(false)
  const [assetToWithdraw, setAssetToWithdraw] = useState<WithdrawableAsset>({
    maxAmount: '',
    network: 'Ethereum'
  })

  const [assetToConvertFrom, setAssetToConvertFrom] = useState<IPaymentMethod | null>(null)
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [conversionPairs, setConversionPairs] = useState<ConversionPair[]>([])

  const [swapModalOpen, setSwapModalOpen] = useState(false)
  const [swapAmount, setSwapAmount] = useState<string>('')
  const [swapFromBalance, setSwapFromBalance] = useState<string>('')
  const [selectedSwapInto, setSelectedSwapInto] = useState<ManagedPaymentMethod | null>(null)
  const [swapEstimate, setSwapEstimate] = useState<{ estimatedAmount: number } | null>(null)
  const [swapLoading, setSwapLoading] = useState(false)
  const [swapEstimating, setSwapEstimating] = useState(false)

  useEffect(() => {
    Treasury.loadBalances()

    const fetchConversionPairs = async (): Promise<void> => {
      try {
        const response = await Radom.getConversionPairs()
        setConversionPairs(response)
      } catch (err) {
        console.error('Failed to get conversion pairs', err.response?.data?.error || err.message)
      }
    }

    fetchConversionPairs()
  }, [])

  const onClickWithdraw = (w: WithdrawableAsset): void => {
    setAssetToWithdraw(w)
    setModalOpen(true)
  }

  const onOpenConvert = (w: WithdrawableAsset): void => {
    setAssetToConvertFrom(w)
    setConvertModalOpen(true)
  }

  const onOpenSwap = (w: WithdrawableAsset): void => {
    setAssetToConvertFrom(w)
    setSwapAmount(w.maxAmount)
    setSwapFromBalance(w.maxAmount)
    setSwapEstimate(null)
    setSwapModalOpen(true)
  }

  const possibleIntoAssets = conversionPairs
    .filter(({ from }) => assetToConvertFrom && isSamePaymentMethod(from, assetToConvertFrom))
    .map(({ into }) => into)

  const swapTargetMethods = useMemo(
    () => getPaymentMethods().filter(m =>
      m.isSwapEnabled &&
      ((testMode && m.isTestnet) || (!testMode && !m.isTestnet)) &&
      !m.isFiat &&
      !m.isHidden &&
      !m.isDisabled
    ),
    [testMode]
  )

  const swapFromMethod = assetToConvertFrom ? getMethod(assetToConvertFrom.network, assetToConvertFrom.token) : null
  const swapFromConversion = swapFromMethod
    ? Treasury.priceQuotes.find(p => p.from.toLowerCase() === swapFromMethod.ticker.toLowerCase())
    : undefined
  const swapMaxAmountNumber = Number(swapFromBalance || 0)
  const swapAmountNumber = Number(swapAmount || 0)

  const recalculateSwapEstimate = async (nextAmount: string, nextInto: ManagedPaymentMethod | null): Promise<void> => {
    if (!assetToConvertFrom || !nextInto || !swapFromConversion) {
      setSwapEstimate(null)
      return
    }

    const numeric = Number(nextAmount)
    if (!nextAmount || Number.isNaN(numeric) || numeric <= 0) {
      setSwapEstimate(null)
      return
    }

    const fiatValue = numeric * swapFromConversion.value
    if (fiatValue < 0) {
      setSwapEstimate(null)
      return
    }

    setSwapEstimating(true)
    try {
      const estimate = await performSwapEstimate({
        fromNetwork: assetToConvertFrom.network,
        fromToken: assetToConvertFrom.token,
        toNetwork: nextInto?.hostChain?.name ?? nextInto?.name,
        toToken: nextInto?.hostChain?.tokenAddress ?? undefined,
        amount: numeric
      })
      setSwapEstimate(estimate)
    } finally {
      setSwapEstimating(false)
    }
  }

  return <>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <PageHeader title='Balances' subTitle='Manage your balances' />
      <Dropdown
        noChevron
        selectedContent={<Dots width={15} />}
        dropdownContent={
          <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
            <DropdownItem onClick={() => setExportBalanceModalOpen(true)}>Export balance report</DropdownItem>
          </div>
        }
      />
    </div>
    <Table
      borderless
      headers={['Asset', 'Balance', 'Exchange rate', ''].map(h => <span key={h}>{h}</span>)}
      items={
        Treasury.managedBalances
          .filter(b => Number(b.balance) > 0)
          .filter(b => {
            const m = getMethod(b.network, b.token)?.isTestnet
            return testMode ? m : !m
          })
          .sort((a, b) => {
            const conversionA = Treasury.priceQuotes
              .find(p => p.from.toLowerCase() === getMethod(a.network, a.token).ticker.toLowerCase())
            const conversionB = Treasury.priceQuotes
              .find(p => p.from.toLowerCase() === getMethod(b.network, b.token).ticker.toLowerCase())

            const aV = Number(a.balance) * (conversionA?.value || 1)
            const bV = Number(b.balance) * (conversionB?.value || 1)

            return bV - aV
          }).map(b => {
            const method = getMethod(b.network, b.token)
            if (b.network === 'Fiat') {
              method.currencyName = b.token
              method.isFiat = true
            }
            const conversion = Treasury.priceQuotes
              .find(p => p.from.toLowerCase() === method.ticker.toLowerCase())

            const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
            const balanceFiatValue = conversion ? Number(b.balance) * (conversion.value) : 0

            return [
              <div key="token" style={{ display: 'flex', flexDirection: 'column', fontSize: 14 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <PaymentMethodDisplay method={{ network: b.network, token: b.token }} excludeName size={30} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TokenTitle>{method.currencyName ?? method.name}</TokenTitle>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      {
                        !method.isFiat && <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                          {method.ticker} {method.isFiat ? undefined : `on ${method.hostChain?.name ?? method.name}`}
                        </span>
                      }
                    </div>
                  </div>
                </div>
              </div>,
              <div key="value" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {
                  new Intl.NumberFormat('en-US', {
                    maximumSignificantDigits: 18
                  }).format(b.balance as any)
                }
                {
                  conversion
                    ? <span style={{ opacity: 0.5 }}>
                      ≈ {formatCurrency(Number(b.balance) * (conversion.value), User.organization?.defaultCurrency ?? 'USD')}
                    </span>
                    : <LoadingBar style={{ height: 10 }} />
                }
              </div>,

              <div key='conversion'>
                {
                  conversion
                    ? <div style={{
                      color: RADOM_COLORS.BLACK,
                      borderRadius: 5,
                      padding: '0 5px'
                    }}>
                      {formatCurrency(Number(conversion.value), User.organization?.defaultCurrency ?? 'USD')} {User.organization?.defaultCurrency ?? 'USD'}
                    </div>
                    : <LoadingBar style={{ height: 16 }} />
                }
              </div>,

              <div key="withdraw" style={{ display: 'flex', gap: '8px' }}>
                {
                  !method.isFiat &&
                  <PrimaryButton
                    onClick={() => {
                      onClickWithdraw({
                        network: b.network,
                        token: b.token,
                        maxAmount: b.balance
                      })
                    }}
                    style={{
                      gap: 5
                    }}>
                    Withdraw
                  </PrimaryButton>
                }
                { User.organization?.features?.isSwapEnabled && !method.isFiat && method.isSwapEnabled && (
                  balanceFiatValue > 0
                    ? (
                      <SecondaryButton
                        onClick={() => {
                          onOpenSwap({
                            network: b.network,
                            token: b.token,
                            maxAmount: b.balance
                          })
                        }}
                        style={{
                          gap: 5
                        }}>
                        Swap
                      </SecondaryButton>
                    )
                    : (
                      <Tooltip message={`Minimum amount to swap is 100 ${defaultCurrency}`}>
                        <SecondaryButton
                          disabled
                          style={{
                            gap: 5
                          }}>
                          Swap
                        </SecondaryButton>
                      </Tooltip>
                    )
                )}
                {
                  User.organization?.features?.isConversionEnabled &&
                  conversionPairs.map(({ from }) => from).find(x => isSamePaymentMethod(x, b)) && // check if there is an available conversion
                  <Tooltip message='Convert balance'>
                    <SecondaryButton
                      onClick={() => {
                        onOpenConvert({
                          network: b.network,
                          token: b.token,
                          maxAmount: b.balance
                        })
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 16,
                        padding: 12,
                        border: 0,
                        backgroundColor: RADOM_COLORS.GRAY_DARK
                      }}>
                      <ConvertIcon
                        stroke={RADOM_COLORS.NICE_BLUE}
                        strokeWidth={2.1}
                        style={{ width: 15, height: 15 }} />
                    </SecondaryButton>
                  </Tooltip>
                }
              </div>
            ]
          })
      }
      isLoading={Treasury.loading}
      emptyPlaceholderText="No funds yet"
    />

    {
      modalOpen &&
      <AssetWithdrawalModal
        modalOpen={modalOpen}
        onModalOpen={open => setModalOpen(open)}
        assetToWithdraw={assetToWithdraw}
      />
    }

    {
      exportBalanceModalOpen &&
      <Modal visible={exportBalanceModalOpen} onClose={() => setExportBalanceModalOpen(false)} title='Export balance report'>
        <ExportBalanceForm />
      </Modal>
    }

    {
      swapModalOpen && assetToConvertFrom &&
      <Modal
        visible={swapModalOpen}
        onClose={() => setSwapModalOpen(false)}
        title='Swap assets'>
        <div style={{ padding: '0px 30px 30px 30px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* From card */}
          <div style={{
            borderRadius: 16,
            padding: 16,
            backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, opacity: 0.7 }}>From</span>
              <span style={{ fontSize: 12, opacity: 0.7 }}>
                Balance: {swapFromBalance}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <input
                  value={swapAmount}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const raw = e.target.value
                    const numeric = Number(raw)
                    let clamped = raw

                    if (!raw) {
                      setSwapAmount('')
                      setSwapEstimate(null)
                      return
                    }

                    if (!Number.isNaN(numeric) && numeric > swapMaxAmountNumber) {
                      clamped = swapFromBalance
                    }

                    setSwapAmount(clamped)
                    setSwapEstimate(null)
                    await recalculateSwapEstimate(clamped, selectedSwapInto)
                  }}
                  placeholder='0.0'
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontSize: 24,
                    outline: 'none'
                  }}
                />
                <button
                  type="button"
                  onClick={async () => {
                    const clamped = swapFromBalance
                    setSwapAmount(clamped)
                    setSwapEstimate(null)
                    await recalculateSwapEstimate(clamped, selectedSwapInto)
                  }}
                  style={{
                    border: 'none',
                    borderRadius: 999,
                    padding: '4px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    backgroundColor: RADOM_COLORS.GRAY9
                  }}>
                  Max
                </button>
              </div>
              <PaymentMethodDisplay
                method={{ network: assetToConvertFrom.network, token: assetToConvertFrom.token }}
                size={36}
                excludeName
              />
            </div>
          </div>

          {/* To card */}
          <div style={{
            borderRadius: 16,
            padding: 16,
            backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, opacity: 0.7 }}>To</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 24 }}>
                  {swapEstimate
                    ? new Intl.NumberFormat('en-US', { maximumSignificantDigits: 18 }).format(swapEstimate.estimatedAmount)
                    : '--'}
                </span>
              </div>
              <Dropdown
                selectedContent={
                  selectedSwapInto
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PaymentMethodDisplay
                          method={{ network: selectedSwapInto.hostChain?.name ?? selectedSwapInto.name, token: selectedSwapInto.hostChain?.tokenAddress }}
                          paymentMethod={selectedSwapInto}
                          size={32}
                          excludeName
                        />
                        <span style={{ fontSize: 14 }}>{selectedSwapInto.ticker}</span>
                      </span>
                    : <span style={{ fontSize: 14 }}>Select currency</span>
                }
                dropdownContent={
                  <div>
                    {swapTargetMethods.map((m, idx) => (
                      <DropdownItem
                        key={`${m.name}-${m.ticker}-${idx}`}
                        onClick={async () => {
                          setSelectedSwapInto(m)
                          setSwapEstimate(null)
                          await recalculateSwapEstimate(swapAmount, m)
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <PaymentMethodDisplay
                            method={{ network: m.hostChain?.name ?? m.name, token: m.hostChain?.tokenAddress }}
                            paymentMethod={m}
                            excludeName
                            size={24}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: 14 }}>{m.currencyName ?? m.name}</span>
                            <span style={{ fontSize: 12, opacity: 0.6 }}>{m.ticker}</span>
                          </div>
                        </div>
                      </DropdownItem>
                    ))}
                  </div>
                }
              />
            </div>
          </div>

          <PrimaryButton
            isLoading={swapLoading}
            disabled={
              swapLoading ||
              swapEstimating ||
              !assetToConvertFrom ||
              !selectedSwapInto ||
              !swapEstimate
            }
            style={{ width: '100%' }}
            onClick={async () => {
              if (!assetToConvertFrom || !selectedSwapInto || !swapEstimate) return
              setSwapLoading(true)
              try {
                await performSwap({
                  fromNetwork: assetToConvertFrom.network,
                  fromToken: assetToConvertFrom.token,
                  toNetwork: selectedSwapInto?.hostChain?.name ?? selectedSwapInto?.name,
                  toToken: selectedSwapInto?.hostChain?.tokenAddress ?? undefined,
                  amount: Number(swapAmount)
                });
                successToast('Swap completed')
                setSwapModalOpen(false)
                Treasury.loadBalances()
              } catch (err) {
                errorToast(err.response?.data?.error || err.message)
              } finally {
                setSwapLoading(false)
              }
            }}>
            Swap
          </PrimaryButton>
        </div>
      </Modal>
    }

    {
      convertModalOpen && assetToConvertFrom && possibleIntoAssets &&
      <AssetConversionRFQModal
        modalOpen={convertModalOpen}
        onClose={() => setConvertModalOpen(false)}
        fromAsset={assetToConvertFrom}
        availableIntoAssets={possibleIntoAssets}
        selectedIntoAsset={possibleIntoAssets[0]}
        onConvert={async (quoteId) => {
          try {
            await Radom.acceptQuote(quoteId)
            successToast('Successfully converted assets')
            setConvertModalOpen(false)
            Treasury.loadBalances()
          } catch (err) {
            errorToast(err.response?.data?.error || err.message)
          }
        }}
      />
    }
  </>
})

export default Overview
